import { ReactNode, useRef, useEffect } from "react";
import { useFocusTrap } from "../../hooks/useFocusTrap";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Accessible label for the dialog */
  title: string;
  children: ReactNode;
  maxWidth?: string;
}

/**
 * Overlay modal with focus trap and keyboard-close (Escape).
 * Focus returns to the previously focused element when closed.
 */
export function Modal({ open, onClose, title, children, maxWidth = "360px" }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="confirm-overlay"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="confirm-dialog"
        style={{ maxWidth }}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
